import { Modal } from 'bootstrap'
import { ErrorApi, normalizarContrasenaPegada } from '../identidad/cliente-identidad.js'
import { notificar } from '../fundacion/alertas.js'
import {
  listarInstituciones,
  listarJornadas,
  listarPeriodos,
  listarProgramas,
  listarSedes,
  type Institucion,
  type Jornada,
  type Periodo,
  type Programa,
  type Sede,
} from '../catalogos/cliente-catalogos.js'
import {
  cambiarEstadoActor,
  cambiarEstadoCoordinador,
  crearActor,
  listarActores,
  reasignar,
  type Actor,
  type TipoActor,
} from './cliente-actores.js'

const elemento = <T extends Element>(selector: string): T => {
  const valor = document.querySelector<T>(selector)
  if (!valor) throw new Error(`No existe ${selector}`)
  return valor
}
let tipos: TipoActor[] = []
let actores: Actor[] = []
let programas: Programa[] = []
let periodos: Periodo[] = []
let instituciones: Institucion[] = []
let sedes: Sede[] = []
let jornadas: Jornada[] = []
let tipoListaPersonal: 'ASESOR' | 'TITULAR' = 'ASESOR'
let filtroPersonal = ''
let esVistaCoordinadorAsesores = false
let esVistaCoordinadorGeneral = false
const alerta = elemento<HTMLElement>('#alerta-actores')
const modal = new Modal(elemento('#modal-reasignacion'))
const informar = (mensaje: string, tipo = 'success'): void => {
  alerta.className = 'alert d-none'
  notificar(mensaje, tipo === 'danger' ? 'error' : (tipo as 'success' | 'warning' | 'info'))
}
const error = (dato: unknown): void =>
  informar(
    dato instanceof ErrorApi ? dato.message : 'No fue posible completar la operación.',
    'danger',
  )
const opcion = (valor: string, texto: string): HTMLOptionElement => {
  const dato = document.createElement('option')
  dato.value = valor
  dato.textContent = texto
  return dato
}
const llenar = (selector: string, datos: Array<{ id: string; nombre: string }>): void =>
  elemento<HTMLSelectElement>(selector).replaceChildren(
    opcion('', 'Seleccione…'),
    ...datos.map((x) => opcion(x.id, x.nombre)),
  )
const nombre = (a: Actor): string => `${a.nombres} ${a.apellidos}`

function ajustarFormulario(): void {
  const tipo = elemento<HTMLSelectElement>('#tipo-actor').value as TipoActor
  elemento('#campo-programa-actor').classList.toggle('d-none', tipo !== 'COORDINADOR_ASESORES')
  elemento('#campo-institucion-actor').classList.toggle(
    'd-none',
    !['TITULAR', 'ESTUDIANTE'].includes(tipo),
  )
  elemento('#campos-estudiante').classList.toggle('d-none', tipo !== 'ESTUDIANTE')
}
function actualizarDependenciasEstudiante(): void {
  const institucionId = elemento<HTMLSelectElement>('#institucion-actor').value
  llenar(
    '#sede-estudiante',
    sedes.filter((dato) => dato.estado === 'ACTIVO' && dato.institucionId === institucionId),
  )
  llenar(
    '#titular-estudiante',
    actores
      .filter(
        (dato) =>
          dato.institucionId === institucionId && !dato.estudianteId && dato.estado === 'ACTIVO',
      )
      .map((dato) => ({ id: dato.id, nombre: nombre(dato) })),
  )
  actualizarJornadasEstudiante()
}
function actualizarJornadasEstudiante(): void {
  const sedeId = elemento<HTMLSelectElement>('#sede-estudiante').value
  llenar(
    '#jornada-estudiante',
    jornadas
      .filter((dato) => dato.estado === 'ACTIVO' && dato.sedeId === sedeId)
      .map((dato) => ({ id: dato.id, nombre: dato.nombre === 'MANANA' ? 'Mañana' : 'Tarde' })),
  )
}
function renderizar(): void {
  const cuerpo = elemento<HTMLTableSectionElement>('#tabla-actores')
  const esCoordinadorAsesores = esVistaCoordinadorAsesores
  const esListaCoordinadores = esVistaCoordinadorGeneral
  const encabezado = cuerpo.closest('table')!.querySelector('thead tr')!
  encabezado.innerHTML = esCoordinadorAsesores
    ? '<th>Nombre</th><th>Institución Educativa</th><th>Cantidad de estudiantes asignados</th><th>Estado</th><th>Acciones</th>'
    : esListaCoordinadores
      ? '<th>Nombre</th><th>Programa académico</th><th>Estado</th><th>Acciones</th>'
      : '<th>Rol</th><th>Tipo</th><th>Programa/Institución</th><th>Asignación</th><th>Estado</th><th>Acciones</th>'
  const mostrarAsignacion = !esCoordinadorAsesores && !esListaCoordinadores
  const listado = esCoordinadorAsesores
    ? actores.filter((actor) => {
        const coincideTipo =
          tipoListaPersonal === 'ASESOR'
            ? Boolean(actor.programaId && !actor.institucionId && !actor.estudianteId)
            : Boolean(actor.institucionId && !actor.estudianteId)
        const texto =
          `${actor.nombres} ${actor.apellidos} ${actor.correo} ${actor.institucionNombre ?? ''}`.toLocaleLowerCase()
        return (
          coincideTipo && (!filtroPersonal || texto.includes(filtroPersonal.toLocaleLowerCase()))
        )
      })
    : actores
  cuerpo.replaceChildren(
    ...listado.map((actor) => {
      const fila = document.createElement('tr')
      const valores = [
        nombre(actor),
        actor.estudianteId
          ? 'Estudiante'
          : actor.institucionId
            ? 'Titular'
            : actor.asesorAsignacionVersion === null && actor.titularAsignacionVersion === null
              ? 'Equipo'
              : tipos.includes('COORDINADOR_ASESORES')
                ? 'Coordinador de asesores'
                : 'Asesor',
        actor.programaNombre ?? actor.institucionNombre ?? '',
        actor.estado,
      ]
      if (esCoordinadorAsesores) {
        for (const valor of [
          nombre(actor),
          tipoListaPersonal === 'TITULAR'
            ? (actor.institucionNombre ?? 'Sin institución')
            : (actor.programaNombre ?? ''),
          String(actor.estudiantesAsignados ?? 0),
        ]) {
          const td = document.createElement('td')
          td.textContent = valor
          fila.append(td)
        }
      } else if (esListaCoordinadores) {
        for (const valor of [nombre(actor), actor.programaNombre ?? '', actor.estado]) {
          const td = document.createElement('td')
          td.textContent = valor
          fila.append(td)
        }
      }
      for (const valor of esCoordinadorAsesores || esListaCoordinadores ? [] : valores) {
        const td = document.createElement('td')
        td.textContent = valor
        fila.append(td)
      }
      if (mostrarAsignacion) {
        const td = document.createElement('td')
        td.textContent = actor.estudianteId
          ? `${actor.asesorNombre ?? 'Sin asesor'} / ${actor.titularNombre ?? 'Sin titular'}`
          : ''
        fila.insertBefore(td, fila.lastElementChild)
      }
      const acciones = document.createElement('td')
      if (actor.estudianteId) {
        acciones.append(botonReasignar(actor, 'asesor'), botonReasignar(actor, 'titular'))
      }
      {
        const tipo = esListaCoordinadores
          ? 'COORDINADOR_ASESORES'
          : actor.estudianteId
            ? 'ESTUDIANTE'
            : actor.institucionId
              ? 'TITULAR'
              : 'ASESOR'
        const boton = document.createElement('button')
        boton.type = 'button'
        boton.className = 'btn btn-sm btn-outline-secondary ms-1'
        boton.textContent = actor.estado === 'ACTIVO' ? 'Inactivar' : 'Activar'
        boton.addEventListener(
          'click',
          () =>
            void (
              esListaCoordinadores
                ? cambiarEstadoCoordinador(actor)
                : cambiarEstadoActor(tipo as Exclude<TipoActor, 'COORDINADOR_ASESORES'>, actor)
            )
              .then(() => cargar('Estado actualizado.'))
              .catch(error),
        )
        acciones.append(boton)
      }
      fila.append(acciones)
      return fila
    }),
  )
}
function botonReasignar(actor: Actor, tipo: 'asesor' | 'titular'): HTMLButtonElement {
  const boton = document.createElement('button')
  boton.type = 'button'
  boton.className = 'btn btn-sm btn-outline-primary me-1'
  boton.textContent = tipo === 'asesor' ? 'Asesor' : 'Titular'
  boton.addEventListener('click', () => {
    elemento<HTMLInputElement>('#tipo-reasignacion').value = tipo
    elemento<HTMLInputElement>('#estudiante-reasignacion').value = actor.id
    const disponibles = actores.filter(
      (a) =>
        a.estado === 'ACTIVO' &&
        (tipo === 'asesor'
          ? a.programaId && !a.institucionId && !a.estudianteId
          : a.institucionId && !a.estudianteId),
    )
    llenar(
      '#nuevo-actor-reasignacion',
      disponibles.map((a) => ({ id: a.id, nombre: nombre(a) })),
    )
    modal.show()
  })
  return boton
}
export async function abrirReasignacionEstudiante(
  estudianteId: string,
  tipo: 'asesor' | 'titular',
): Promise<void> {
  if (!actores.some((actor) => actor.estudianteId === estudianteId)) {
    const respuesta = await listarActores('ESTUDIANTE')
    actores = [...actores, ...respuesta.datos]
  }
  const actor = actores.find((dato) => dato.estudianteId === estudianteId)
  if (!actor) throw new Error('No fue posible encontrar el estudiante.')
  const disponibles = await listarActores(tipo === 'asesor' ? 'ASESOR' : 'TITULAR')
  actores = [...actores, ...disponibles.datos]
  botonReasignar(actor, tipo).click()
}

export async function cargar(mensaje?: string): Promise<void> {
  filtroPersonal = ''
  elemento<HTMLInputElement>('#filtro-personal').value = ''
  const [p, pe, i, s, j, ...listas] = await Promise.all([
    listarProgramas(),
    listarPeriodos(),
    listarInstituciones(),
    listarSedes(),
    listarJornadas(),
    ...tipos.map(listarActores),
  ])
  programas = p.datos
  periodos = pe.datos
  instituciones = i.datos
  sedes = s.datos
  jornadas = j.datos
  actores = listas.flatMap((x) => x.datos)
  llenar(
    '#programa-actor',
    programas.filter((x) => x.estado === 'ACTIVO'),
  )
  llenar(
    '#institucion-actor',
    instituciones.filter((x) => x.estado === 'ACTIVO'),
  )
  llenar(
    '#periodo-estudiante',
    periodos.filter((x) => x.estado === 'ACTIVO').map((x) => ({ id: x.id, nombre: x.codigo })),
  )
  const asesores = actores.filter(
    (a) => a.programaId && !a.institucionId && !a.estudianteId && a.estado === 'ACTIVO',
  )
  llenar(
    '#asesor-estudiante',
    asesores.map((a) => ({ id: a.id, nombre: nombre(a) })),
  )
  actualizarDependenciasEstudiante()
  renderizar()
  if (mensaje) informar(mensaje)
}
export function configurarInterfazActores(roles: string[]): void {
  // Si una cuenta tiene ambos roles, prevalece el alcance institucional del
  // Coordinador General: nunca debe mostrar las listas propias del
  // Coordinador de Asesores (asesores, titulares y estudiantes).
  esVistaCoordinadorGeneral = roles.includes('COORDINADOR_GENERAL')
  esVistaCoordinadorAsesores = !esVistaCoordinadorGeneral && roles.includes('COORDINADOR_ASESORES')
  tipos = esVistaCoordinadorGeneral
    ? ['COORDINADOR_ASESORES']
    : esVistaCoordinadorAsesores
      ? ['ASESOR', 'TITULAR', 'ESTUDIANTE']
      : []
  const esCoordinador =
    roles.includes('COORDINADOR_GENERAL') || roles.includes('COORDINADOR_ASESORES')
  elemento<HTMLElement>('#etiqueta-menu-actores').textContent = esCoordinador
    ? 'Personal Pedagógico'
    : 'Catálogos'
  elemento<HTMLElement>('#titulo-actores').textContent = esCoordinador
    ? 'Personal Pedagógico'
    : 'Catálogos'
  const pestanasPersonal = elemento('#pestanas-personal')
  pestanasPersonal.classList.toggle('d-none', !esCoordinador)
  elemento('#pestanas-tipo-personal').classList.toggle('d-none', !esVistaCoordinadorAsesores)
  elemento('#panel-lista-personal').classList.add('d-none')
  elemento('#pestana-crear-personal').classList.add('active')
  elemento('#pestana-lista-personal').classList.remove('active')
  elemento('#panel-crear-personal').classList.remove('d-none')
  elemento('#pestana-crear-personal').textContent = esVistaCoordinadorGeneral
    ? 'Crear Coordinador de asesores'
    : 'Crear Personal'
  elemento('#pestana-lista-personal').textContent = esVistaCoordinadorGeneral
    ? 'Lista de Coordinadores de asesores'
    : 'Lista Personal Pedagógico'
  elemento<HTMLSelectElement>('#tipo-actor').replaceChildren(
    ...tipos.map((t) =>
      opcion(
        t,
        t === 'COORDINADOR_ASESORES'
          ? 'Coordinador de asesores'
          : t.charAt(0) + t.slice(1).toLowerCase(),
      ),
    ),
  )
  ajustarFormulario()
}

elemento<HTMLSelectElement>('#tipo-actor').addEventListener('change', ajustarFormulario)
elemento<HTMLSelectElement>('#institucion-actor').addEventListener(
  'change',
  actualizarDependenciasEstudiante,
)
elemento<HTMLSelectElement>('#sede-estudiante').addEventListener(
  'change',
  actualizarJornadasEstudiante,
)
elemento<HTMLButtonElement>('#pestana-crear-personal').addEventListener('click', () => {
  elemento('#pestana-crear-personal').classList.add('active')
  elemento('#pestana-lista-personal').classList.remove('active')
  elemento('#panel-crear-personal').classList.remove('d-none')
  elemento('#panel-lista-personal').classList.add('d-none')
})
elemento<HTMLButtonElement>('#pestana-lista-personal').addEventListener('click', () => {
  const formulario = elemento<HTMLFormElement>('#formulario-actor')
  formulario.reset()
  formulario.classList.remove('was-validated')
  elemento('#pestana-lista-personal').classList.add('active')
  elemento('#pestana-crear-personal').classList.remove('active')
  elemento('#panel-lista-personal').classList.remove('d-none')
  elemento('#panel-crear-personal').classList.add('d-none')
  renderizar()
})
elemento<HTMLButtonElement>('#pestana-asesores').addEventListener('click', () => {
  elemento<HTMLFormElement>('#formulario-filtro-personal').reset()
  filtroPersonal = ''
  tipoListaPersonal = 'ASESOR'
  elemento('#pestana-asesores').classList.add('active')
  elemento('#pestana-titulares').classList.remove('active')
  renderizar()
})
elemento<HTMLButtonElement>('#pestana-titulares').addEventListener('click', () => {
  elemento<HTMLFormElement>('#formulario-filtro-personal').reset()
  filtroPersonal = ''
  tipoListaPersonal = 'TITULAR'
  elemento('#pestana-titulares').classList.add('active')
  elemento('#pestana-asesores').classList.remove('active')
  renderizar()
})
elemento<HTMLFormElement>('#formulario-filtro-personal').addEventListener('submit', (evento) => {
  evento.preventDefault()
  filtroPersonal = elemento<HTMLInputElement>('#filtro-personal').value.trim()
  renderizar()
})
elemento<HTMLFormElement>('#formulario-actor').addEventListener('submit', (evento) => {
  evento.preventDefault()
  const forma = evento.currentTarget as HTMLFormElement
  if (!forma.checkValidity()) {
    forma.classList.add('was-validated')
    return
  }
  const tipo = elemento<HTMLSelectElement>('#tipo-actor').value as TipoActor
  const base = {
    nombres: elemento<HTMLInputElement>('#nombres-actor').value,
    apellidos: elemento<HTMLInputElement>('#apellidos-actor').value,
    correo: elemento<HTMLInputElement>('#correo-actor').value,
    celular: elemento<HTMLInputElement>('#celular-actor').value || undefined,
    contrasena: normalizarContrasenaPegada(elemento<HTMLInputElement>('#contrasena-actor').value),
  }
  const entrada =
    tipo === 'COORDINADOR_ASESORES'
      ? { ...base, programaId: elemento<HTMLSelectElement>('#programa-actor').value }
      : tipo === 'TITULAR'
        ? { ...base, institucionId: elemento<HTMLSelectElement>('#institucion-actor').value }
        : tipo === 'ESTUDIANTE'
          ? {
              ...base,
              institucionId: elemento<HTMLSelectElement>('#institucion-actor').value,
              periodoId: elemento<HTMLSelectElement>('#periodo-estudiante').value,
              sedeId: elemento<HTMLSelectElement>('#sede-estudiante').value,
              jornadaId: elemento<HTMLSelectElement>('#jornada-estudiante').value,
              asesorId: elemento<HTMLSelectElement>('#asesor-estudiante').value,
              titularId: elemento<HTMLSelectElement>('#titular-estudiante').value,
            }
          : base
  void crearActor(tipo, entrada)
    .then(() => {
      forma.reset()
      ajustarFormulario()
      return cargar('Rol creado correctamente.')
    })
    .catch(error)
})
elemento<HTMLFormElement>('#formulario-reasignacion').addEventListener('submit', (evento) => {
  evento.preventDefault()
  const estudiante = actores.find(
    (a) => a.id === elemento<HTMLInputElement>('#estudiante-reasignacion').value,
  )
  if (!estudiante) return
  const tipo = elemento<HTMLInputElement>('#tipo-reasignacion').value as 'asesor' | 'titular'
  void reasignar(
    tipo,
    estudiante,
    elemento<HTMLSelectElement>('#nuevo-actor-reasignacion').value,
    elemento<HTMLTextAreaElement>('#motivo-reasignacion').value,
  )
    .then(() => {
      modal.hide()
      return cargar('Reasignación registrada con historia.')
    })
    .catch(error)
})
